import { ErrorHandler, Inject, Injectable, Injector } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'

import { ErrorService } from './error.service'
import { ToastrService } from 'ngx-toastr'
import { ApolloError } from '@apollo/client/core'
import { LogActionService } from './log-action/log-action.service'
import { TargetTypes } from 'src/generated/graphql'
import { IndividualConfig } from 'ngx-toastr/toastr/toastr-config'

interface ErrorData {
  title: string
  message: string
  stackTrace: string
  notifierOverrides?: Partial<IndividualConfig>
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // Error handling is important and needs to be loaded first.
  // Because of this we should manually inject the services with Injector.
  constructor(@Inject(Injector) private injector: Injector) {}

  handleError(error: Error | HttpErrorResponse | ApolloError) {
    if ((error as any)?.networkError?.error == 'No token') {
      console.log('Managed to catch the issue and return')
      return
    }
    const errorData = this.getErrorData(error)
    if (errorData) {
      const logActionService = this.injector.get(LogActionService)
      const notifier = this.injector.get(ToastrService)
      if (errorData.title === 'Unknown Error') {
        notifier.error('The server is down! Please try again later.', 'Server down!', errorData.notifierOverrides)
      } else {
        notifier.error(errorData.message, errorData.title, errorData.notifierOverrides)
      }
      logActionService.logError(TargetTypes.Unknown, '', {
        extra: {
          title: errorData.title,
          message: errorData.message,
          stackTrace: errorData.stackTrace,
          url: window.location.href,
        },
      })
    }
    console.error(error)
  }

  private getErrorData(error: Error | HttpErrorResponse | ApolloError): ErrorData | undefined {
    if (error instanceof ApolloError) {
      return this.getErrorDataFromApolloError(error)
    } else if (error instanceof HttpErrorResponse) {
      return this.getErrorDataFromHttpErrorResponse(error)
    } else {
      return this.getErrorDataFromError(error)
    }
  }

  getErrorDataFromApolloError(error: ApolloError): ErrorData | undefined {
    if (error.networkError) {
      const networkError = error.networkError as HttpErrorResponse
      return {
        title: networkError.statusText,
        message: networkError.message,
        stackTrace: '',
      }
    } else {
      const errorObject = error.graphQLErrors[0].extensions.response as any
      return {
        title: errorObject?.error ?? 'Unknown error',
        message: errorObject?.message ?? 'Unknown error! Please try again later.',
        stackTrace: '',
      }
    }
  }

  getErrorDataFromHttpErrorResponse(error: HttpErrorResponse): ErrorData | undefined {
    // Server Error
    const errorService = this.injector.get(ErrorService)
    const message = errorService.getServerMessage(error)
    const stackTrace = errorService.getServerStack(error)
    if (error.error) {
      return {
        title: error.error.error,
        message: error.error.message,
        stackTrace,
        notifierOverrides: { onActivateTick: true },
      }
    } else {
      return {
        title: '',
        message: message,
        stackTrace,
        notifierOverrides: { onActivateTick: true },
      }
    }
  }

  getErrorDataFromError(error: Error): ErrorData | undefined {
    const errorService = this.injector.get(ErrorService)
    const message = errorService.getClientMessage(error)
    const stackTrace = errorService.getClientStack(error)
    if (message && message !== 'Unauthorized') {
      return {
        title: '',
        message: message,
        stackTrace,
        notifierOverrides: { onActivateTick: true },
      }
    } else {
      return undefined
    }
  }
}
